import { toTokenUnits } from "../../../../utils/decimalString";
import {
  GetMarkets200Response,
  PostRfqsPayload,
  PostRfqsPayloadLegsInner,
  SideResponse,
} from "../../../../codegen-api";
import { getUsdcDecimals } from "../../../../config/chains";
import { IFormStrategy, IFormValues, IStrategy } from "./types";

export const emptyStrategy: IFormStrategy = {
  asset: undefined,
  side: undefined,
  instrument_type: undefined,
  option_type: undefined,
  expiry: undefined,
  strike: undefined,
  ratio: "1",
  isEdit: true,
};

export const optionAssets: string[] = ["ETH", "BTC"];

export const defaultValues = {
  full_size: true,
  amount: "",
  strategies: [emptyStrategy] as Array<IFormStrategy>,
};

/** The 0.25 grid a strategy leg's ratio sits on. */
export const RFQ_RATIO_STEP = 0.25;
/** The 0.1 grid a block size sits on. */
export const RFQ_SIZE_STEP = 0.1;

/** A block size in USDC base units, floored to USDC's decimals. */
export const rfqAmountUnits = (amount: string): string => {
  const units = toTokenUnits(amount, getUsdcDecimals());
  if (!units) throw new Error(`Invalid block size "${amount}".`);
  return units.toString();
};

export function buildRfqPayload(
  formStrategies: IFormValues,
  activePerpMarkets: GetMarkets200Response[] | undefined,
  activeOptionMarkets: GetMarkets200Response[] | undefined
): PostRfqsPayload {
  const { full_size, amount, strategies } = formStrategies;

  // Map formstrategies based on strike, expiry and asset,
  // find the market and then convert this into PostQuotesPayload
  const legs = strategies.map((strategy: IStrategy) => {
    const markets = [
      ...(activePerpMarkets || []),
      ...(activeOptionMarkets || []),
    ];
    const market = markets?.find(
      (m) =>
        m.underlying_asset === strategy.asset &&
        m.instrument_type === strategy.instrument_type &&
        m.option_type === strategy.option_type &&
        m.expiry === strategy.expiry &&
        m.strike === strategy.strike
    );

    // If either one of the values are undefined, remove
    if (
      market === undefined ||
      market.instrument_id === undefined ||
      strategy.ratio === undefined ||
      strategy.ratio.length === 0 ||
      strategy.side === undefined
    ) {
      return undefined;
    }

    return {
      instrument: market.instrument_id,
      is_buy: Boolean(strategy.side === SideResponse.Buy),
      ratio: Number(strategy.ratio),
    } as PostRfqsPayloadLegsInner;
  });

  const payload: PostRfqsPayload = {
    full_size,
    // `toTokenUnits`, not `parseUnits`: the latter THROWS on text with more
    // decimals than USDC has, and on exponent form ("5e-7").
    amount: rfqAmountUnits(amount),
    legs: legs.filter(
      (leg): leg is PostRfqsPayloadLegsInner => leg !== undefined
    ),
    duration: 1800,
  };

  return payload;
}
